import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { JobProgressPayload, JobCompletedPayload, JobFailedPayload } from '@/types/ws';
import type { RootState } from '@/store/store';

/** Job states where the job is no longer actively running. */
export const TERMINAL_JOB_STATES = new Set([
  'completed',
  'failed',
  'discarded',
  'retryable',
  'cancelled',
]);

export interface JobState {
  id: string;
  worker: string;
  state: string;
  pct: number;
  message: string;
  completedAt: string | null;
}

interface JobsState {
  activeJobs: Record<string, JobState>;
}

const initialState: JobsState = {
  activeJobs: {},
};

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    jobEnqueued(state, action: PayloadAction<{ jobId: string; worker: string; state: string }>) {
      state.activeJobs[action.payload.jobId] = {
        id: action.payload.jobId,
        worker: action.payload.worker,
        state: action.payload.state,
        pct: 0,
        message: 'Queued',
        completedAt: null,
      };
    },
    jobProgressReceived(state, action: PayloadAction<JobProgressPayload>) {
      const existing = state.activeJobs[action.payload.job_id];
      if (existing) {
        existing.state = action.payload.state;
        existing.pct = action.payload.pct;
        existing.message = action.payload.message;
      } else {
        // Upsert: job started elsewhere (pipeline/test/workflow) — create placeholder
        state.activeJobs[action.payload.job_id] = {
          id: action.payload.job_id,
          worker: 'unknown',
          state: action.payload.state,
          pct: action.payload.pct,
          message: action.payload.message,
          completedAt: null,
        };
      }
    },
    jobCompleted(state, action: PayloadAction<JobCompletedPayload>) {
      const job = state.activeJobs[action.payload.job_id];
      if (job) {
        job.state = action.payload.state;
        job.pct = 100;
        job.message = 'Complete';
        job.completedAt = action.payload.completed_at;
      }
    },
    jobFailed(state, action: PayloadAction<JobFailedPayload>) {
      const job = state.activeJobs[action.payload.job_id];
      if (job) {
        job.state = action.payload.state;
        job.message = action.payload.error_message ?? 'Failed';
        job.completedAt = action.payload.failed_at;
      }
    },
    jobRemoved(state, action: PayloadAction<string>) {
      const updated: Record<string, JobState> = {};
      for (const [id, job] of Object.entries(state.activeJobs)) {
        if (id !== action.payload) updated[id] = job;
      }
      state.activeJobs = updated;
    },
  },
});

export const { jobEnqueued, jobProgressReceived, jobCompleted, jobFailed, jobRemoved } =
  jobsSlice.actions;

export const selectActiveJobs = (state: RootState) => state.jobs.activeJobs;

export default jobsSlice.reducer;
